import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useQueryState, parseAsString } from 'nuqs'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { IScene, IScript } from '@/types/cinematics'
import { Login, Register, Container, Pair, JoinnusAuth, Callback, Redirect, ChangePassword, ResetPassword } from './Forms'
import { AuthLayout } from './Layout/AuthLayout'
import { toSafeInternalPath } from '@/utils/url'
import { LAYOUT_TYPE, AUTH_FORM_TYPES, STANDARD_AUTH_FORMS, AUTH_FLOW_MUTATION_KEY, LOGIN_ROUTE } from '@/constants/auth'
import { LayoutType, IAuthScriptOptions, FormOption } from './types'
import { useUser } from '@/hooks/users/useUser'
import { useAuthTheme } from '@/actors/AuthActor/hooks/useAuthTheme'
import { FormLayout } from '@/actors/AuthActor/Layout/FormLayout'
import { useOnboardingRedirect } from '@/hooks/auth/useOnboardingRedirect'
import { useForceLogout } from '@/hooks/auth/useForceLogout'
import { decideFormRecovery, resolveFormFallbacks } from '@/actors/AuthActor/utils/resolveFormFallbacks'
import { isOnboardingRoute } from '@/constants/onboarding'
import { useEntitlementCapture } from '@/hooks/activation/useEntitlementCapture'
import { useActivationLinkingFailure } from '@/hooks/activation/useActivationLinkingFailure'
import { clearLinkingFailure, consumeLinkingFailure } from '@/utils/activation'
import * as Sentry from '@sentry/nextjs'
import { useIsMutating } from '@tanstack/react-query'
import { ActivationErrorModal } from '@/actors/AuthActor/components/ActivationErrorModal'
import { useAuth } from '@/hooks/auth/useAuth'
import { AuthSuccessModal } from '@/actors/AuthActor/components/AuthSuccessModal'


const HOME_ROUTE = '/'

/** Where the activation modal's TERMINATED exit sends the user to subscribe
 *  directly. Owned here, next to the navigation mechanics, rather than in the
 *  modal — the modal only reports which exit was clicked. */
const ACTIVATION_PLANS_ROUTE = '/checkout'

export interface AuthActorProps {
  changePasswordHash?: string
  compact?: boolean
  /** Mirrors the page's own `Layout hasNavbar` when the scene does not set it. */
  hasNavbar?: boolean
  initialAuthForm?: string
  initialLayoutType: LayoutType
  pairPath?: string
  redirectOnAuthenticated?: boolean
  // AuthActor is reused as an embedded sub-component (checkout, redeem, my-team)
  // with a synthetic script that has no actorId, so accept a partial script here.
  script: Partial<IScript> & { options?: IAuthScriptOptions }
  set?: IScene['set']
}

export const AuthActor: React.FC<AuthActorProps> = (props) => {
  const {
    changePasswordHash,
    compact = false,
    hasNavbar,
    initialAuthForm,
    initialLayoutType = LAYOUT_TYPE.ISLAND,
    pairPath,
    redirectOnAuthenticated = true,
    script: { options },
    set,
  } = props
  const pendingPairCode = useRef<string | null>(null)
  // Persists an activation `entitlementId` from the URL (PRO-13). Observe-only:
  // no state, no URL writes, a no-op when the param is absent — see
  // docs/ACTIVATION.md.
  useEntitlementCapture()
  const { isLoggedIn, isLoading } = useUser()
  const router = useRouter()
  // A pending linking failure (PRO-15): auth succeeded but the activation did
  // not link. While one is blocking, the post-auth redirect and its loading
  // overlay hold so the per-reason modal can offer an exit; acknowledging via
  // any modal button consumes the record (if terminal) and clears the store,
  // and the redirect resumes. Embedded panels never block — an Entel message
  // must not interrupt a purchase — and clear the failure on sight without
  // consuming, so the record keeps its retry and a later non-embedded auth
  // both retries and informs (see effects below).
  const linkingFailure = useActivationLinkingFailure()
  const { logoutMutation } = useAuth()
  // The modal must not render over a live auth flow: register's onSuccess
  // awaits the auto-login (which can itself clear or re-set the failure), and
  // the form-level LoadingOverlay ($overlay-z-index) sits above the modal
  // ($modal-z-index) for that whole window. Gating on the flow mutations'
  // shared key defers the modal — and its Clarity event — until the outcome
  // is final and the overlay is gone.
  const isAuthFlowPending = useIsMutating({ mutationKey: [...AUTH_FLOW_MUTATION_KEY] }) > 0
  // Embedded panels (checkout, redeem, my-team, CheckoutV2) mount this actor
  // with the *shared* `Auth` scene's script via useActorScript, so every
  // scene-level policy option has to be ignored for them — otherwise a flag set
  // for a partner route silently rewrites the purchase flow. These callers are
  // exactly the ones passing `compact` / `redirectOnAuthenticated={false}`.
  const isEmbedded = compact || !redirectOnAuthenticated

  // A page prop wins: /pair, /callback, /reset-password and /user/recover all
  // render this actor against the shared `Auth` scene, and their form is
  // structural — a `defaultForm` on that scene must not send a pair deep link
  // to the login form (which would also clear `isPairRoute` below and break
  // device pairing outright). Pages with no structural form — the catch-all —
  // omit the prop and let the scene choose. RedeemAuth and MyTeamActor pass no
  // prop either, hence the embedded guard: they must not inherit it.
  const sceneDefaultForm = isEmbedded ? undefined : options?.defaultForm
  const defaultForm = initialAuthForm ?? sceneDefaultForm ?? STANDARD_AUTH_FORMS.MAIN
  const [rawForm, setForm] = useQueryState('form', parseAsString.withDefault(defaultForm))
  const form = rawForm || defaultForm
  const [pairCode] = useQueryState('code', parseAsString.withDefault(pairPath || ''))
  const theme = useAuthTheme({ options, activeForm: form })
  const isPairForm = form === STANDARD_AUTH_FORMS.PAIR
  // Track if we're on a pair route (user navigated to /pair), regardless of current form state
  const isPairRoute = defaultForm === STANDARD_AUTH_FORMS.PAIR
  const { fallbackForm, recoveryForm } = resolveFormFallbacks({ form, defaultForm, isLoggedIn })

  const defaultLayout = options?.defaultLayout || initialLayoutType || LAYOUT_TYPE.ISLAND
  const onAuthenticated = options?.onAuthenticated
  const blockingLinkingFailure = isEmbedded ? null : linkingFailure
  // Sign-in-only scenes (TV pairing): federated sign-in must not silently create
  // an account. Enforced by the backend — the flag rides along with the idToken.
  // Never in an embedded panel: blocking registration inside checkout would kill
  // the sign-up half of the purchase flow.
  const denyRegister = !isEmbedded && (options?.denyRegister ?? false)
  const isStandardForm = Object.values(STANDARD_AUTH_FORMS).includes(form as any)
  const currentForm = options?.layout?.[form as keyof typeof options || STANDARD_AUTH_FORMS.MAIN]
  const isGenericForm = !isStandardForm && currentForm?.type
  const { getNextOnboardingRoute, isIdentified, profile } = useOnboardingRedirect()
  // Partner scenes opt into a clean session via the cinematic. Inert (settled on
  // first render) for every scene that does not set the flag, and never in an
  // embedded panel — a `forceLogout` meant for a partner route would otherwise
  // sign the buyer out mid-purchase.
  //
  // Two gates, deliberately: the overlay drops on the hook's wall-clock cap so a
  // hung logout cannot strand the visitor, while the redirect waits for the
  // session to be *known* resolved.
  const {
    isSettled: isSessionSettled,
    isRedirectAllowed: isSessionRedirectAllowed
  } = useForceLogout({
    enabled: (options?.forceLogout ?? false) && !isEmbedded,
  })

  const [layoutType, setLayoutType] = useState(defaultLayout)
  const [isPairConfirmed, setIsPairConfirmed] = useState(false)

  const handleLayoutTypeChange = useCallback((type: LayoutType | null) => {
    if (type === LAYOUT_TYPE.ISLAND) return
    setLayoutType(type || defaultLayout)
  }, [defaultLayout])

  // Falls back to the scene's own landing form, not a hardcoded 'main': a scene
  // that sets `defaultForm` need not define a `main` layout at all, and sending
  // it there would render an empty card with no way back.
  const handleNavigateToForm = useCallback((form: string) => {
    setForm(form || fallbackForm)
  }, [setForm, fallbackForm])

  const handleActivationContinue = useCallback(() => {
    consumeLinkingFailure()
    clearLinkingFailure()
  }, [])

  // Navigate CTA (View plans): the failure stays SET during the push so the
  // redirect effect stays suppressed and cannot race this navigation with the
  // scene's onAuthenticated route; the unmount cleanup below clears it once
  // the destination scene replaces this actor.
  const handleActivationViewPlans = useCallback(() => {
    consumeLinkingFailure()
    router.push(ACTIVATION_PLANS_ROUTE)
  }, [router])

  // ALREADY_CLAIMED exit: the subscription lives on another account, so a
  // live session is the wrong one — sign it out, then land on the recovery
  // form. Anonymous is a real state here (register linked-failed but its
  // auto-login also failed): skip the logout, there is no session to drop.
  // The failure clears only after the logout settles: clearing first would
  // re-arm the redirect while the session is still live.
  const handleActivationRecoverAccess = useCallback(async () => {
    if (isLoggedIn) {
      try {
        await logoutMutation.mutateAsync()
      } catch (error) {
        // Keep the modal up (buttons re-enable when isPending drops) so the
        // action can be retried. The mutation's onError only emits a GTM
        // event — this report is the ops signal for a broken recovery exit.
        Sentry.captureException(error, {
          level: 'warning',
          tags: { activationAuthFlow: 'recover-access' },
        })
        return
      }
    }

    consumeLinkingFailure()
    clearLinkingFailure()
    setForm(STANDARD_AUTH_FORMS.RESET_PASSWORD)
  }, [isLoggedIn, logoutMutation, setForm])

  const handlePairConfirmed = () => setIsPairConfirmed(true)

  // Pairing can end this web session (PRO-794), which swaps the Pair form for
  // the login fallback; the pair code must not survive into that relogin or it
  // reopens the pair form, prefilled with a used code, right after login.
  const handlePairSuccessDismiss = () => {
    if (isLoggedIn) {
      router.push(HOME_ROUTE)
      return
    }

    pendingPairCode.current = null
    router.push(LOGIN_ROUTE)
  }

  // Embedded panels suppress the modal (an Entel message must not interrupt a
  // purchase) and drop the failure on sight — WITHOUT consuming, so the
  // stored record keeps its retry and the user is informed on the next
  // non-embedded auth. Sentry already has the report either way.
  useEffect(() => {
    if (isEmbedded && linkingFailure) {
      clearLinkingFailure()
    }
  }, [isEmbedded, linkingFailure])

  // Non-embedded mounts clear (never consume) any pending failure on unmount:
  // a modal abandoned via browser Back or a navbar link must not go stale in
  // the module store and resurface over a later, unrelated auth route. The
  // record's kept/consumed state already encodes whether the next auth
  // retries, so nothing is lost with the surface.
  useEffect(() => {
    if (isEmbedded) return undefined

    return () => clearLinkingFailure()
  }, [isEmbedded])

  // Handle pair code detection and storage
  useEffect(() => {
    // If we have a pair code (from props or URL), store it in our ref
    if (pairCode) {
      pendingPairCode.current = pairCode
    }
  }, [pairCode])

  useEffect(() => {
    // We avoid evaluating redirects while the general authentication system is loading
    // Or while useOnboardingRedirect is identifying the user
    // Or while a forced logout is still tearing the previous session down — the
    // redirect would otherwise fire on the stale session and bounce the visitor
    // off the partner scene before the logout lands. This uses the redirect gate,
    // not `isSessionSettled`: the latter also opens on the hook's timeout cap,
    // which is exactly the case where the cached session cannot be trusted.
    if (isLoading || !isIdentified || !redirectOnAuthenticated || !isSessionRedirectAllowed) {
      return
    }

    // PRO-15: a pending linking failure holds the redirect so the modal can
    // offer its exit. Continue/Recover clear it to resume; View plans pushes
    // its own route while the failure is still set (so this effect cannot
    // race it) and relies on the unmount cleanup to clear.
    if (blockingLinkingFailure) {
      return
    }

    const hasPendingPairCode = !!pendingPairCode.current || !!pairCode

    if (isLoggedIn) {

      // 1. Priority: Pair Device
      if (hasPendingPairCode && !isPairForm) {
        setForm(STANDARD_AUTH_FORMS.PAIR)
        return
      }

      // 2. Adaptive Onboarding Redirect
      if (!hasPendingPairCode && !isPairRoute && onAuthenticated) {
        let targetRoute = onAuthenticated

        if (isOnboardingRoute(onAuthenticated)) {
          const onboardingRoute = getNextOnboardingRoute()

          if (onboardingRoute === null) {
            return
          }

          targetRoute = onboardingRoute
        }

        // `onAuthenticated` is remote scene config — keep the post-auth
        // redirect same-origin; an off-origin value falls back to home.
        const safeRoute = toSafeInternalPath(targetRoute) || HOME_ROUTE
        sessionStorage.setItem('auth_scene_redirect', safeRoute)
        router.push(safeRoute)
        return
      }

      if (isPairRoute && !isPairForm) {
        setForm(STANDARD_AUTH_FORMS.PAIR)
        return
      }
    } else {
      if (isPairForm) {
        setForm(fallbackForm)
        return
      }
    }
  }, [
    isLoggedIn,
    isLoading,
    isIdentified,
    form,
    pairCode,
    onAuthenticated,
    redirectOnAuthenticated,
    router,
    setForm,
    isPairRoute,
    isPairForm,
    isSessionRedirectAllowed,
    blockingLinkingFailure,
    fallbackForm,
    getNextOnboardingRoute,
    profile
  ])

  // Recovery for a `?form=` the cinematic does not define. Two cases, both
  // landing on the scene's own default rather than a hardcoded 'main':
  //  - a non-standard form key with no `layout` entry (bad/stale URL);
  //  - 'main' on a scene that defines no `main` layout, which a Back button
  //    reaches by default and which would otherwise render an empty card with
  //    no way out.
  const isUndefinedForm = !isStandardForm && !currentForm?.type
  const isMissingMain = form === STANDARD_AUTH_FORMS.MAIN
    && !currentForm
    && defaultForm !== STANDARD_AUTH_FORMS.MAIN

  // Bounded by `decideFormRecovery` so a scene that is dead at both ends of the
  // hop cannot bounce forever — see that helper for the full rule.
  const isDeadForm = isUndefinedForm || isMissingMain
  const recoveredFormsRef = useRef<string[]>([])

  useEffect(() => {
    const decision = decideFormRecovery(recoveredFormsRef.current, form, isDeadForm)
    recoveredFormsRef.current = decision.recoveredForms

    if (decision.shouldRecover) {
      handleNavigateToForm(recoveryForm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeadForm, recoveryForm, form])

  // Show loading overlay during initial load, while a forced logout is in
  // flight, or when redirecting (logged in but not on pair form)
  // The redirect-wait clause also requires a destination: a scene without
  // options.onAuthenticated never navigates (the effect has no else), and
  // masking it would latch the overlay forever — most reachable right after
  // the activation modal's Continue on a misauthored partner scene.
  const shouldShowLoading = isLoading || !isSessionSettled ||
    (isLoggedIn && !isPairForm && redirectOnAuthenticated && !!onAuthenticated && !blockingLinkingFailure)

  return (
    <>
      <LoadingOverlay isLoading={shouldShowLoading} />
      {blockingLinkingFailure && !isAuthFlowPending && (
        <ActivationErrorModal
          failure={blockingLinkingFailure}
          isBusy={logoutMutation.isPending}
          onContinue={handleActivationContinue}
          onViewPlans={handleActivationViewPlans}
          onRecoverAccess={handleActivationRecoverAccess}
        />
      )}
      <AuthLayout
        type={layoutType}
        set={set}
        style={theme.layout}
        vars={theme.vars}
        hasNavbar={hasNavbar}
      >
        <FormLayout
          form={(currentForm || {}) as FormOption}
          headerImage={isEmbedded ? undefined : theme.headerImage}
          headerImageWidth={isEmbedded ? undefined : theme.headerImageWidth}
          headerImageHeight={isEmbedded ? undefined : theme.headerImageHeight}
          compact={compact}
        >
          {/* Outside the form switch: a session ended by the pairing unmounts
              Pair before the user has read the confirmation. */}
          <AuthSuccessModal
            isVisible={isPairConfirmed}
            type={isLoggedIn ? 'success-pair' : 'success-pair-relogin'}
            onDismiss={handlePairSuccessDismiss}
          />
          {form === STANDARD_AUTH_FORMS.MAIN && (
            <Container
              form={(currentForm || {}) as FormOption}
              handleNavigateToForm={handleNavigateToForm}
              denyRegister={denyRegister}
            />
          )}
          {form === STANDARD_AUTH_FORMS.LOGIN && (
            <Login
              form={(currentForm || {}) as FormOption}
              handleNavigateToForm={handleNavigateToForm}
            />
          )}
          {form === STANDARD_AUTH_FORMS.REGISTER && (
            // On a denyRegister scene the register formChoice is hidden, but a
            // direct ?form=register would still render the form — fall back to
            // Login so the scene stays sign-in-only. (Authoritative enforcement
            // is server-side: the backend refuses account creation when
            // denyRegister travels with the auth — see the note in useAuth.)
            denyRegister ? (
              <Login
                form={(currentForm || {}) as FormOption}
                handleNavigateToForm={handleNavigateToForm}
              />
            ) : (
              <Register
                form={(currentForm || {}) as FormOption}
                handleNavigateToForm={handleNavigateToForm}
                termsAndConditions={options?.termsAndConditions}
              />
            )
          )}
          {form === STANDARD_AUTH_FORMS.RESET_PASSWORD && (
            <ResetPassword
              handleBack={() => handleNavigateToForm(STANDARD_AUTH_FORMS.LOGIN)}
              layoutType={layoutType}
              handleLayoutTypeChange={handleLayoutTypeChange}
            />
          )}
          {form === STANDARD_AUTH_FORMS.CHANGE_PASSWORD && (
            <ChangePassword
              handleBack={() => handleNavigateToForm(STANDARD_AUTH_FORMS.LOGIN)}
              changePasswordHash={changePasswordHash || ''}
              layoutType={layoutType}
              handleLayoutTypeChange={handleLayoutTypeChange}
            />
          )}
          {form === STANDARD_AUTH_FORMS.PAIR && (
            <Pair
              pairCode={pairCode}
              onPaired={handlePairConfirmed}
            />
          )}
          {form === STANDARD_AUTH_FORMS.JOINNUS && (
            <JoinnusAuth
              handleNavigateToForm={handleNavigateToForm}
              form={(currentForm || {}) as FormOption}
            />
          )}

          {(!isStandardForm && isGenericForm) &&
            currentForm?.type === AUTH_FORM_TYPES.CONTAINER &&
            <Container
              compact={compact}
              form={(currentForm || {}) as FormOption}
              handleNavigateToForm={handleNavigateToForm}
              denyRegister={denyRegister}
            />
          }
          {(!isStandardForm && isGenericForm) &&
            currentForm?.type === AUTH_FORM_TYPES.CALLBACK &&
            <Callback
              handleLayoutTypeChange={handleLayoutTypeChange}
              handleNavigateToForm={handleNavigateToForm}
              form={(currentForm || {}) as FormOption}
            />
          }
          {(!isStandardForm && isGenericForm) &&
            currentForm?.type === AUTH_FORM_TYPES.REDIRECT &&
            <Redirect
              handleLayoutTypeChange={handleLayoutTypeChange}
              form={(currentForm || {}) as FormOption}
            />
          }
        </FormLayout>
      </AuthLayout>
    </>
  )
}